import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DefaultSupervisionService } from '../../../services/default-supervision.service';
import {
  DefaultSupervisionContact,
  DefaultSupervisionMetrikTypeEnum,
  DefaultSupervisionNodeTypeEnum,
  DefaultSupervisionScopeEnum,
  DefaultSupervisionTreeNode,
} from '../../../models/default-supervision.model';
import { DefaultSupervisionAssignModalComponent } from './default-supervision-assign-modal/default-supervision-assign-modal.component';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { getAvatarColor, getInitials } from '../../../utils/avatar.util';

/** Ligne aplatie du tableau arborescent (niveau 2 = catégorie/type, niveau 3 = générique). */
interface DefaultSupervisionRow {
  node: DefaultSupervisionTreeNode;
  parent?: DefaultSupervisionTreeNode;
  level: 2 | 3;
}

/**
 * Tableau arborescent des acteurs « Supervision métier » par défaut (PRED-3061) :
 * bascule Bâtiments/Équipements, recherche standard et badges de provenance
 * (direct / hérité du parent / configuration client).
 * Utilisé par l'onglet paramètres client (scope tenant) et la fiche établissement
 * (scope establishment, prioritaire sur le tenant).
 */
@Component({
  selector: 'default-supervision-tree',
  templateUrl: './default-supervision-tree.component.html',
  styleUrls: ['./default-supervision-tree.component.scss'],
})
export class DefaultSupervisionTreeComponent implements OnInit, OnChanges {
  @Input() scope: DefaultSupervisionScopeEnum = DefaultSupervisionScopeEnum.TENANT;
  @Input() establishment?: { _id: string; label: string; technicalIdentifier?: string };
  /** Permissions requises (OU) pour affecter/modifier/retirer un contact. Absent = pas de garde. */
  @Input() modifyPermissions?: ENUM_PERMISSIONS[];

  loading = false;
  tree: DefaultSupervisionTreeNode[] = [];
  rows: DefaultSupervisionRow[] = [];
  displayedColumns = ['node', 'contact', 'provenance', 'actions'];

  metrikType: DefaultSupervisionMetrikTypeEnum = DefaultSupervisionMetrikTypeEnum.BUILDING;
  search = '';
  private expandedKeys = new Set<string>();

  DefaultSupervisionScopeEnum = DefaultSupervisionScopeEnum;
  DefaultSupervisionMetrikTypeEnum = DefaultSupervisionMetrikTypeEnum;

  /** Palette plus sombre propre à l'arborescence (passée au helper d'avatar partagé). */
  private readonly avatarPalette = ['#00608a', '#7c3aed', '#0e7490', '#b45309', '#15803d', '#9d174d', '#4338ca'];

  /** Icône Material par type de nœud (fallback générique). */
  private static readonly NODE_TYPE_ICONS: Record<DefaultSupervisionNodeTypeEnum, string> = {
    [DefaultSupervisionNodeTypeEnum.GENERIC_BUILDING_CATEGORY]: 'domain',
    [DefaultSupervisionNodeTypeEnum.GENERIC_BUILDING]: 'meeting_room',
    [DefaultSupervisionNodeTypeEnum.EQUIPMENT_TYPE]: 'precision_manufacturing',
    [DefaultSupervisionNodeTypeEnum.GENERIC_EQUIPMENT]: 'build',
  };

  /**
   * Icône Material par catégorie générique de bâtiment, clé = libellé normalisé
   * (majuscules, sans accent — voir normalizeLabel).
   */
  private static readonly CATEGORY_ICONS: Record<string, string> = {
    BATI: 'domain',
    EXTERIEUR: 'nature_people',
  };

  /** Icône par défaut si le type de nœud est inconnu. */
  private static readonly DEFAULT_NODE_ICON = 'category';

  /** Plage Unicode des marques diacritiques combinantes (accents décomposés par NFD). */
  private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

  /** Normalise un libellé pour servir de clé : trim + majuscules + suppression des accents. */
  private static normalizeLabel(label: string): string {
    return label.trim().toUpperCase().normalize('NFD').replace(DefaultSupervisionTreeComponent.DIACRITICS, '');
  }

  constructor(
    private defaultSupervisionService: DefaultSupervisionService,
    private dialog: MatDialog,
  ) {}

  get isEstablishmentScope(): boolean {
    return this.scope === DefaultSupervisionScopeEnum.ESTABLISHMENT;
  }

  ngOnInit(): void {
    if (!this.isEstablishmentScope) {
      this.loadTree();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.establishment && this.establishment?._id) {
      this.loadTree();
    }
  }

  loadTree(): void {
    this.loading = true;
    this.defaultSupervisionService.getTree(this.isEstablishmentScope ? this.establishment?._id : undefined).subscribe({
      next: result => {
        this.tree = result.data;
        this.buildRows();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  // ------------------------------------------------------------------
  // Construction des lignes visibles (aplatissement + filtres client)
  // ------------------------------------------------------------------

  setMetrikType(metrikType: DefaultSupervisionMetrikTypeEnum): void {
    this.metrikType = metrikType;
    this.buildRows();
  }

  onSearchChange(search: string): void {
    this.search = search ?? '';
    this.buildRows();
  }

  toggleExpand(row: DefaultSupervisionRow): void {
    const key = this.nodeKey(row.node);
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
    this.buildRows();
  }

  isExpanded(row: DefaultSupervisionRow): boolean {
    return this.expandedKeys.has(this.nodeKey(row.node));
  }

  buildRows(): void {
    const root = this.tree.find(node => node.metrikType === this.metrikType);
    const rows: DefaultSupervisionRow[] = [];
    const search = this.search.trim().toLowerCase();
    const filterActive = !!search;

    for (const parent of root?.children ?? []) {
      if (filterActive) {
        // En recherche : tout est déplié, on montre les parents pertinents et leurs enfants correspondants
        const children = (parent.children ?? []).filter(child => this.matchesFilters(child, search));
        const parentMatches = parent.assignable && this.matchesFilters(parent, search);
        if (!parentMatches && children.length === 0) continue;
        rows.push({ node: parent, level: 2 });
        for (const child of children) {
          rows.push({ node: child, parent, level: 3 });
        }
      } else {
        rows.push({ node: parent, level: 2 });
        if (this.expandedKeys.has(this.nodeKey(parent))) {
          for (const child of parent.children ?? []) {
            rows.push({ node: child, parent, level: 3 });
          }
        }
      }
    }
    this.rows = rows;
  }

  private matchesFilters(node: DefaultSupervisionTreeNode, search: string): boolean {
    if (!search) return true;
    const contact = this.displayedContact(node);
    return (
      node.label.toLowerCase().includes(search) || (contact && this.contactName(contact).toLowerCase().includes(search))
    );
  }

  private nodeKey(node: DefaultSupervisionTreeNode): string {
    return node._id ?? `special-${node.metrikType}-${node.label}`;
  }

  hasChildren(row: DefaultSupervisionRow): boolean {
    return (row.node.children?.length ?? 0) > 0;
  }

  /**
   * Icône à afficher pour un nœud :
   * - 'folder_off' si non assignable ;
   * - pour une catégorie générique de bâtiment, icône dédiée selon le libellé (CATEGORY_ICONS) ;
   * - sinon icône du type de nœud, puis icône par défaut.
   */
  nodeIcon(node: DefaultSupervisionTreeNode): string {
    if (!node.assignable) return 'folder_off';

    if (node.nodeType === DefaultSupervisionNodeTypeEnum.GENERIC_BUILDING_CATEGORY) {
      const key = DefaultSupervisionTreeComponent.normalizeLabel(node.label);
      const categoryIcon = DefaultSupervisionTreeComponent.CATEGORY_ICONS[key];
      if (categoryIcon) return categoryIcon;
    }

    return node.nodeType
      ? DefaultSupervisionTreeComponent.NODE_TYPE_ICONS[node.nodeType]
      : DefaultSupervisionTreeComponent.DEFAULT_NODE_ICON;
  }

  // ------------------------------------------------------------------
  // Affichage contact / provenance
  // ------------------------------------------------------------------

  displayedContact(node: DefaultSupervisionTreeNode): DefaultSupervisionContact | undefined {
    return node.ownContact ?? node.inheritedContact;
  }

  contactName(contact: DefaultSupervisionContact): string {
    return `${contact.firstName} ${contact.lastName}`;
  }

  contactInitials(contact: DefaultSupervisionContact): string {
    return getInitials(contact.firstName, contact.lastName);
  }

  avatarColor(contact: DefaultSupervisionContact): string {
    return getAvatarColor(contact._id, this.avatarPalette);
  }

  provenance(row: DefaultSupervisionRow): { label: string; kind: 'own' | 'parent' | 'tenant' | 'missing' } | null {
    const node = row.node;
    if (!node.assignable) return null;
    if (node.ownContact) {
      return { label: this.isEstablishmentScope ? 'Établissement' : 'Direct', kind: 'own' };
    }
    if (node.inheritedContact) {
      switch (node.inheritedFrom) {
        case 'parent':
          return { label: `Hérité — ${row.parent?.label ?? 'nœud parent'}`, kind: 'parent' };
        case 'tenant':
          return { label: 'Config client', kind: 'tenant' };
        case 'tenantParent':
          return { label: `Config client — ${row.parent?.label ?? 'nœud parent'}`, kind: 'tenant' };
      }
    }
    return { label: 'Non couvert', kind: 'missing' };
  }

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  editNode(node: DefaultSupervisionTreeNode): void {
    this.openAssignModal(node, 'assign');
  }

  clearNode(node: DefaultSupervisionTreeNode): void {
    this.openAssignModal(node, 'unassign');
  }

  private openAssignModal(node: DefaultSupervisionTreeNode, mode: 'assign' | 'unassign'): void {
    this.dialog
      .open(DefaultSupervisionAssignModalComponent, {
        width: '650px',
        data: {
          mode,
          node,
          scope: this.scope,
          establishment: this.establishment,
        },
      })
      .afterClosed()
      .subscribe(updated => {
        if (updated) {
          this.loadTree();
        }
      });
  }
}
